import { useCallback, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { Button, Popover, Text } from "@/atoms"
import { useLogger } from "@/hooks/useLogger"
import { useNumberConvertor } from "@/hooks/useNumberConvertor"
import { useTranslation } from "@/hooks/useTranslation"
import { QuickWinBolt } from "@/icons"
import { QUICK_WINS_DRAWER_ID } from "@/organisms/Drawers/const"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"

import styles from "./QuickWinProjectBadge.module.scss"

import type { QuickWinProjectBadgeProps } from "./QuickWinProjectBadge.types"

export const QuickWinProjectBadge = ({
  count,
  projectId,
  totalIssues
}: QuickWinProjectBadgeProps) => {
  const { t } = useTranslation()
  const { logger, EVENTS } = useLogger()
  const { abbreviateNumber } = useNumberConvertor()
  const [popoverOpen, setPopoverOpen] = useState(false)
  const { open } = useDrawer(QUICK_WINS_DRAWER_ID)
  const [searchParams] = useSearchParams()

  const openQuickWinsDrawer = useCallback(
    (e: React.SyntheticEvent, source: string) => {
      e.preventDefault()
      e.stopPropagation()
      const qwType = searchParams.get("quickWinsType")
      const meta: Record<string, string> = {
        quickWinsCount: String(count),
        source
      }
      if (qwType === "PACKAGE_UPDATE" || qwType === "BASE_IMAGE_UPDATE") {
        meta.typeFilter = qwType
      }
      open({ record: { id: projectId, meta } })
    },
    [open, searchParams, projectId, count]
  )

  const handleBadgeClick = useCallback(
    (e: React.SyntheticEvent) => {
      setPopoverOpen(false)
      logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.PROJECT.BOLT_BADGE_CLICK, {
        projectId,
        quickWinsCount: count
      })
      openQuickWinsDrawer(e, "bolt_badge")
    },
    [logger, EVENTS, projectId, count, openQuickWinsDrawer]
  )

  const handleShowMeClick = useCallback(
    (e: React.SyntheticEvent) => {
      setPopoverOpen(false)
      logger.info(EVENTS.ANALYTIC_EVENTS.QUICK_WINS.PROJECT.HOVER_SHOW_ME, {
        projectId,
        quickWinsCount: count
      })
      openQuickWinsDrawer(e, "hover_show_me")
    },
    [logger, EVENTS, projectId, count, openQuickWinsDrawer]
  )

  return (
    <Popover
      open={popoverOpen}
      onOpenChange={setPopoverOpen}
      content={
        <div className={styles.tooltip}>
          <Text>
            {t(
              count === 1
                ? "quickWins.inlineBanner.textSingular"
                : "quickWins.inlineBanner.text",
              {
                quickWinsCount: count,
                issuesCount: abbreviateNumber(totalIssues)
              }
            )}
          </Text>
          <Button type="default" size="small" onClick={handleShowMeClick}>
            {t("quickWins.showMe")}
          </Button>
        </div>
      }
    >
      <span
        className={styles.badge}
        onClick={handleBadgeClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            handleBadgeClick(e)
          }
        }}
        aria-label={`${count} ${t(count === 1 ? "quickWins.badge.quickWin" : "quickWins.badge.quickWins")}`}
      >
        <QuickWinBolt
          className={styles.icon}
          fillClassName={styles.iconFill}
          width={14}
          height={14}
        />
        {abbreviateNumber(count)}
      </span>
    </Popover>
  )
}
